import { styled } from "~/common/mui/styled/styled";
import { Card as MuiCard, Divider } from "~/common/mui";
import { C_INFORMATION, C_PRIMARY, C_PRIMARY_DISABLED } from "~/common/components/icon/Colors";

// ============================================================================
// Root Grid Container (10 columns: lineNo + 9 data columns)
// ============================================================================

export const QuoteGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(10, auto)",
  rowGap: theme.spacing(4),

  columnGap: "16px",
  [theme.breakpoints.up("desktop-m")]: {
    columnGap: "24px",
  },
  [theme.breakpoints.up("desktop-l")]: {
    columnGap: "32px",
  },
}));

// ============================================================================
// Header Row
// ============================================================================

export const QuoteHeader = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "subgrid",
  gridColumn: "1 / -1",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),

  "&::after": {
    content: '""',
    display: "block",
    gridColumn: "1 / -1",
    borderBottom: `3px solid ${theme.palette.grey[400]}`,
    marginLeft: theme.spacing(-2),
    marginRight: theme.spacing(-2),
  },
}));

export const HeaderCell = styled("div")<{ isCheckbox?: boolean }>(({ theme, isCheckbox }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  fontWeight: 700,
  padding: `${theme.spacing(1)} 0`,
  wordBreak: "break-word",
  ...(isCheckbox && {
    justifySelf: "flex-end",
  }),
  "&:nth-of-type(2)": {
    paddingLeft: theme.spacing(2),
  },
}));

export const HeaderSubtitle = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 400,
}));

// ============================================================================
// Quote Group (one line number + card)
// Uses position:relative so line numbers can be absolutely positioned
// ============================================================================

export const QuoteGroup = styled("div")(({ theme }) => ({
  position: "relative",
  display: "grid",
  gridTemplateColumns: "subgrid",
  gridColumn: "1 / -1",

  "&:not(:last-child)::after": {
    content: '""',
    display: "block",
    gridColumn: "1 / -1",
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(4),
  },
}));

// Line number cell - absolutely positioned by JavaScript
// Position is calculated based on the target QuotePartsTable element
export const LineNumberCell = styled("div")(({ theme }) => ({
  // Position is set inline by useAbsoluteLineAlignment hook
  // position: "absolute", top: N are applied via style prop
  paddingLeft: theme.spacing(2),
  display: "flex",
  width: "auto",
  zIndex: 1,
}));

// ============================================================================
// Quote Card - spans columns 2-10, uses subgrid for internal alignment
// ============================================================================

export const QuoteCard = styled(MuiCard)(({ theme }) => ({
  gridColumn: "2 / -1",
  display: "grid",
  gridTemplateColumns: "subgrid",
  // Note: gridTemplateRows: "subgrid" removed - Safari has bugs with row subgrid + span
  paddingInline: theme.spacing(2),
  gap: theme.spacing(2),
  position: "relative",
}));

// ============================================================================
// Sections inside Card (use subgrid to maintain column alignment)
// ============================================================================

interface SectionProps {
  indicatorColor?: "main" | "alternative" | "alternativeProduct";
}

const indicatorColors = {
  main: C_PRIMARY, // Green
  alternative: C_PRIMARY_DISABLED, // Light green
  alternativeProduct: C_INFORMATION, // Blue
};

export const QuoteSection = styled("div")<SectionProps>(({ theme, indicatorColor }) => ({
  display: "grid",
  gridTemplateColumns: "subgrid",
  // Note: gridTemplateRows: "subgrid" removed - Safari has bugs with row subgrid + span
  gridColumn: "1 / -1",
  rowGap: theme.spacing(4),
  position: "relative",
  ...(indicatorColor && {
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: -16,
      bottom: 0,
      width: 4,
      backgroundColor: indicatorColors[indicatorColor],
      borderRadius: 2,
    },
  }),
}));

// ============================================================================
// Row inside sections - uses subgrid to align with parent columns
// ============================================================================

export const QuoteRow = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "subgrid",
  gridColumn: "1 / -1",
  alignItems: "start",
}));

export const QuoteCell = styled("div")<{ isCheckbox?: boolean }>(({ theme, isCheckbox }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  justifySelf: isCheckbox ? "flex-end" : "flex-start",
}));

// ============================================================================
// Product Header Area
// ============================================================================

export const ProductHeaderContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "subgrid",
  gridColumn: "1 / -1",
  gap: theme.spacing(2),
  paddingBlock: theme.spacing(2),
}));

export const ProductInfoCell = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

// ============================================================================
// Separator
// ============================================================================

export const RowDivider = styled(Divider)(({ theme }) => ({
  gridColumn: "1 / -1",
}));

// ============================================================================
// Toggle Button for expand/collapse
// ============================================================================

export const ExpandToggle = styled("button")(({ theme }) => ({
  gridColumn: "1 / -1",
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(1),
  background: "none",
  border: "none",
  cursor: "pointer",
  color: theme.palette.primary.main,
  font: "inherit",
  padding: 0,
  textAlign: "left",
}));
